import * as Api from '@ViewModels';
import { ObservablePageCollectionController } from '@ViewModels';
import { computed, makeObservable, observable, override } from 'mobx';
import { SlotMachineGameViewModel } from './slotMachines';

export class GameViewModel<TGameType extends Api.IGame = Api.IGame> extends Api.ViewModel {
	protected mGame: TGameType;

	constructor(userSession: Api.UserSessionContext, game?: TGameType) {
		super(userSession);

		makeObservable<GameViewModel, 'mGame'>(this, {
			mGame: observable.ref,
			id: computed,
			name: computed,
			load: override,
		});

		this.mGame = game;
	}

	public get id() {
		return this.mGame.id;
	}

	public get name() {
		return this.mGame.name;
	}

	public load = async () => {
		if (this.loading) {
			return;
		}

		this.loading = true;
		const opResult = await this.mUserSession.webServiceHelper.callWebServiceAsync<TGameType>(
			this.composeApiUrl({ urlPath: `game/${this.id}` }),
			'GET'
		);

		this.loading = false;

		if (!opResult.success) {
			throw opResult;
		}

		this.mGame = opResult.value;
		this.loaded = true;
		return opResult;
	};

	public toJs = () => {
		return this.mGame;
	};
}

export class GameTemplatesViewModel<TGameType extends Api.IGame = Api.IGame> extends Api.ViewModel {
	private mGameTemplatesPageCollectionController: ObservablePageCollectionController<
		TGameType,
		GameViewModel<TGameType>
	>;

	protected static mCreateGameTemplate = async <T extends Api.IGame = Api.IGame>(
		userSession: Api.UserSessionContext,
		path: string,
		template: Api.IGameTemplate<T>
	) => {
		const opResult = await userSession.webServiceHelper.callWebServiceAsync<Api.IGameTemplate<T>>(
			path,
			'POST',
			template
		);

		if (!opResult.success) {
			throw opResult;
		}

		return opResult;
	};

	public static CreateSlotMachineTemplate = async (
		userSession: Api.UserSessionContext,
		template: Api.IGameTemplate<Api.ISlotMachine>
	) => {
		return GameTemplatesViewModel.mCreateGameTemplate(userSession, 'game/slot-machine/template', template);
	};

	constructor(userSession: Api.UserSessionContext) {
		super(userSession);

		makeObservable<GameTemplatesViewModel<TGameType>, 'mGameTemplatesPageCollectionController'>(this, {
			mGameTemplatesPageCollectionController: observable.ref,
			provider: computed,
		});

		this.mGameTemplatesPageCollectionController = new ObservablePageCollectionController({
			apiPath: `game/template`,
			client: userSession.webServiceHelper,
			transformer: this.mCreateGame,
		});
	}

	public get provider() {
		return this.mGameTemplatesPageCollectionController;
	}

	protected mCreateGame = (gameTemplate: Api.IGameTemplate<TGameType>) => {
		switch (gameTemplate.game._type) {
			case 'SlotMachine': {
				return new SlotMachineGameViewModel(this.mUserSession, gameTemplate.game as any as Api.ISlotMachine);
			}
			default: {
				return new GameViewModel<TGameType>(this.mUserSession, gameTemplate.game);
			}
		}
	};
}
